import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "./AuthContext";
import { toast } from "sonner";

export const SECTION_LABELS = {
  banner: "Ana Banner",
  featured: "Öne Çıkan Projeler",
  packages: "Yatırım Paketleri",
  ecosystem: "Üç Katmanlı Değer",
  process: "Süreç",
  why: "Neden Biz",
  regions: "Bölgeler",
  financial: "Finansal Avantajlar",
  testimonials: "Referanslar",
  cta: "İletişim Çağrısı",
};

const DEFAULT_SCALE = { heading: 1, text: 1, icon: 1, padding: 1 };
const DEFAULT_SECTIONS = Object.keys(SECTION_LABELS).map((key) => ({
  key, visible: true, desktop: { ...DEFAULT_SCALE }, mobile: { ...DEFAULT_SCALE },
}));

const LayoutContext = createContext(null);

export const LayoutProvider = ({ children }) => {
  const { user } = useAuth();
  const location = useLocation();
  const [sections, setSections] = useState(DEFAULT_SECTIONS);
  const [saved, setSaved] = useState(DEFAULT_SECTIONS);
  const [device, setDevice] = useState("desktop");
  const [saving, setSaving] = useState(false);

  const editRequested = new URLSearchParams(location.search).get("edit") === "1";
  const editing = editRequested && !!user && user !== false;

  useEffect(() => {
    api.get("/layout/home")
      .then((r) => { setSections(r.data.sections); setSaved(r.data.sections); })
      .catch(() => {});
  }, []);

  const move = useCallback((key, dir) => {
    setSections((prev) => {
      const i = prev.findIndex((s) => s.key === key);
      const j = i + dir;
      if (i < 0 || j < 0 || j >= prev.length) return prev;
      const next = [...prev];
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });
  }, []);

  const toggle = useCallback((key) => {
    setSections((prev) => prev.map((s) => (s.key === key ? { ...s, visible: !s.visible } : s)));
  }, []);

  const setScale = useCallback((key, field, value) => {
    const safe = Number(value);
    if (!Number.isFinite(safe)) return;
    setSections((prev) => prev.map((s) => (
      s.key === key ? { ...s, [device]: { ...DEFAULT_SCALE, ...s[device], [field]: safe } } : s
    )));
  }, [device]);

  const resetSection = useCallback((key) => {
    setSections((prev) => prev.map((s) => (
      s.key === key ? { ...s, [device]: { ...DEFAULT_SCALE } } : s
    )));
  }, [device]);

  const save = useCallback(async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/home-layout", { sections });
      setSections(data.sections);
      setSaved(data.sections);
      toast.success("Sayfa düzeni kaydedildi");
    } catch (e) {
      toast.error("Kaydedilemedi");
    } finally {
      setSaving(false);
    }
  }, [sections]);

  const discard = useCallback(() => setSections(saved), [saved]);

  const dirty = useMemo(() => JSON.stringify(sections) !== JSON.stringify(saved), [sections, saved]);

  return (
    <LayoutContext.Provider value={{ sections, editing, device, setDevice, move, toggle, setScale, resetSection, save, discard, dirty, saving }}>
      {children}
    </LayoutContext.Provider>
  );
};

export const useLayout = () => useContext(LayoutContext) || {
  sections: [], editing: false, device: "desktop", setDevice: () => {},
  move: () => {}, toggle: () => {}, setScale: () => {}, resetSection: () => {},
  save: () => {}, discard: () => {}, dirty: false, saving: false,
};
